import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HEAT_MAP_CONFIG } from "@/constants/game";

const {
  ZONE_WIDTH, ZONE_HEIGHT, CHIP_RADIUS,
  CANVAS_BLEED, CHIP_COLORS,
} = HEAT_MAP_CONFIG;

// Dock tiles are small (like player tiles on the diamond); the ghost chip that
// appears during drag is full size so it lands accurately in the zone.
const TILE_DOCK_SIZE = 36;   // px — resting tile size in the selector row
const FULL_TILE      = CHIP_RADIUS * 2;  // px — ghost chip + placed chip diameter

// Max px the chip CENTER may drift outside the zone border during active drag.
const DRAG_EDGE_PX = 12;
const DRAG_OV_X    = DRAG_EDGE_PX / ZONE_WIDTH;
const DRAG_OV_Y    = DRAG_EDGE_PX / ZONE_HEIGHT;

const PITCH_TYPES = [
  { abbr: "FB", name: "Fastball" },
  { abbr: "CH", name: "Changeup" },
  { abbr: "CB", name: "Curveball" },
  { abbr: "SL", name: "Slider" },
];

const DOT_R     = 6;
const DOT_SPACE = DOT_R * 2.6;

// Natural layout height — drives the responsive scale calculation.
// Zone + gap + dock row + gap + filter bar + gap + inning bar + status
const NATURAL_H = ZONE_HEIGHT + 14 + TILE_DOCK_SIZE + 8 + 26 + 8 + 26 + 24;

// ── Normalise a history entry to { x, y, chip, inning } ─────────────────────
// Entries may be [x, y, chip] (legacy) or [x, y, chip, inning] (current).
function entryParts(entry) {
  if (Array.isArray(entry)) {
    return { x: entry[0], y: entry[1], chip: entry[2], inning: entry[3] ?? null };
  }
  return { x: entry.x, y: entry.y, chip: entry.chip, inning: entry.inning ?? null };
}

// ── Spread dots so no dot fully covers another ───────────────────────────────
function computeSpreadDots(history) {
  const placed = [];
  for (const entry of history) {
    const { x: hx, y: hy, chip: chipType } = entryParts(entry);
    const origPx = hx * ZONE_WIDTH;
    const origPy = hy * ZONE_HEIGHT;
    let group = null;
    for (const g of placed) {
      if (Math.hypot(origPx - g.anchorX, origPy - g.anchorY) < DOT_SPACE) { group = g; break; }
    }
    if (!group) { group = { anchorX: origPx, anchorY: origPy, members: [] }; placed.push(group); }
    const idx = group.members.length;
    let px, py;
    if (idx === 0) { px = origPx; py = origPy; }
    else {
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      const angle = idx * goldenAngle;
      const r = DOT_R * 2.4 * Math.sqrt(idx);
      px = origPx + Math.cos(angle) * r;
      py = origPy + Math.sin(angle) * r;
    }
    group.members.push({ px, py, chipType });
  }
  return placed.flatMap(g => g.members);
}

// ── Pulsing overlap ring ─────────────────────────────────────────────────────
function PulsingOverlapRing({ posA, posB }) {
  if (!posA || !posB) return null;
  const cx = ((posA.x + posB.x) / 2) * ZONE_WIDTH;
  const cy = ((posA.y + posB.y) / 2) * ZONE_HEIGHT;
  const r = CHIP_RADIUS * 2.0;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      style={{ position: "absolute", left: cx - r, top: cy - r, width: r * 2, height: r * 2, pointerEvents: "none", zIndex: 20 }}
    >
      <motion.div
        animate={{ scale: [1, 1.18, 1], opacity: [0.75, 1, 0.75] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        style={{
          width: "100%", height: "100%", borderRadius: "50%",
          border: "2.5px solid rgba(74,222,128,0.9)",
          background: "rgba(74,222,128,0.08)",
          boxShadow: "0 0 22px rgba(74,222,128,0.55)",
        }}
      />
    </motion.div>
  );
}

// ── Chip shown in reveal panels ──────────────────────────────────────────────
function RevealChip({ pos, chip, abbr, typeMatch }) {
  if (!pos) return null;
  const color = CHIP_COLORS[(chip ?? 1) - 1] || "#ffffff";
  const tx = pos.x * ZONE_WIDTH - CHIP_RADIUS;
  const ty = pos.y * ZONE_HEIGHT - CHIP_RADIUS;
  return (
    <div
      style={{
        position: "absolute", top: 0, left: 0,
        width: CHIP_RADIUS * 2, height: CHIP_RADIUS * 2,
        borderRadius: "50%",
        border: `3px solid ${color}`,
        background: color + "44",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 900, color,
        pointerEvents: "none", zIndex: 10,
        transform: `translate(${tx}px, ${ty}px)`,
        boxShadow: typeMatch
          ? `0 0 20px rgba(251,191,36,0.85), 0 0 8px ${color}88`
          : `0 0 10px ${color}66`,
      }}
    >
      {abbr}
    </div>
  );
}

// ── Own placed chip — GPU-composited via transform only ──────────────────────
function PlacedChip({ pos, selectedChip, locked, dragging, snapping, overhangThreshold }) {
  if (!pos) return null;
  const baseColor = CHIP_COLORS[(selectedChip ?? 1) - 1] || "#94a3b8";
  // Check if position is in danger zone (dragging and overhang threshold exceeded)
  const overX = (overhangThreshold / 100) * (CHIP_RADIUS / ZONE_WIDTH);
  const overY = (overhangThreshold / 100) * (CHIP_RADIUS / ZONE_HEIGHT);
  const inDangerZone = dragging && (
    pos.x < -overX || pos.x > 1 + overX ||
    pos.y < -overY || pos.y > 1 + overY
  );
  const color = inDangerZone ? "#ef4444" : baseColor;
  const tx = pos.x * ZONE_WIDTH - CHIP_RADIUS;
  const ty = pos.y * ZONE_HEIGHT - CHIP_RADIUS;
  return (
    <div
      style={{
        position: "absolute", top: 0, left: 0,
        width: CHIP_RADIUS * 2, height: CHIP_RADIUS * 2,
        borderRadius: "50%",
        border: `3px solid ${color}`,
        background: locked ? color + "55" : color + "99",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 900,
        color: locked ? color : "white",
        pointerEvents: "none", zIndex: 10,
        transform: `translate(${tx}px, ${ty}px) scale(${dragging ? 1.06 : 1})`,
        transition: snapping
          ? "transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)"
          : dragging ? "transform 0s" : "transform 0.08s ease-out",
        willChange: "transform",
        boxShadow: dragging
          ? `0 0 18px ${color}88, 0 4px 14px rgba(0,0,0,0.35)`
          : `0 0 10px ${color}55`,
      }}
    >
      {locked && selectedChip ? PITCH_TYPES[selectedChip - 1]?.abbr : "●"}
    </div>
  );
}

// ── Strike zone grid lines ───────────────────────────────────────────────────
function StrikeZoneGrid() {
  const LINE_COLOR = "rgba(255,255,255,0.22)";
  const fracs = [1 / 3, 2 / 3];
  return (
    <>
      {fracs.map((f, i) => (
        <div key={`h${i}`} style={{
          position: "absolute", top: `${f * 100}%`,
          left: 0, right: 0, height: 1,
          background: LINE_COLOR, pointerEvents: "none",
        }} />
      ))}
      {fracs.map((f, i) => (
        <div key={`v${i}`} style={{
          position: "absolute", left: `${f * 100}%`,
          top: 0, bottom: 0, width: 1,
          background: LINE_COLOR, pointerEvents: "none",
        }} />
      ))}
    </>
  );
}

// ── Shared pill button style helper ─────────────────────────────────────────
function pillStyle(active, color) {
  return {
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 8,
    fontWeight: 800,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "all 0.12s",
    border: `1px solid ${active
      ? (color ? color + "cc" : "rgba(255,255,255,0.55)")
      : "rgba(255,255,255,0.10)"}`,
    background: active
      ? (color ? color + "2a" : "rgba(255,255,255,0.18)")
      : "rgba(2,6,23,0.60)",
    color: active
      ? (color || "#ffffff")
      : "rgba(255,255,255,0.28)",
  };
}

// ── Main component ───────────────────────────────────────────────────────────
export default function HeatMapZone({
  role = "pitcher",
  chipCount = 4,
  selectedChip = null,
  onChipSelect,
  placedPosition = null,
  onPlace,
  onPlaceFinal,   // fires once on pointer-up (drag end / tile drop) — use for auto lock-in
  locked = false,
  revealMode = false,
  pitcherPosition = null,
  batterPosition = null,
  pitcherChip = null,
  batterChip = null,
  positionalOverlap = null,
  typeMatch = null,
  pitchHistory = [],
  depletedChips = null,  // Set<number> of chip indices (1-4) that are unavailable
  overhangThreshold = 20,  // max % of chip radius allowed outside zone boundary
  zoneWidthPct = 100,  // zone width as % of default (60-100)
}) {
  const outerRef  = useRef(null);
  const zoneRef   = useRef(null);
  const canvasRef = useRef(null);
  const [scale, setScale]             = useState(1);
  const [isDragging, setIsDragging]   = useState(false);
  const [tileDrag, setTileDrag]       = useState(null);
  const [revealStage, setRevealStage] = useState(0);

  // DOTS and HEAT are independent toggles — both, one, or neither can be active.
  const [showDots, setShowDots] = useState(true);
  const [showHeat, setShowHeat] = useState(false);

  // Pitch-type filter: Set of chip numbers that are visible (1–4). At least 1 always on.
  const [typeFilters, setTypeFilters] = useState(() => new Set([1, 2, 3, 4]));

  // Inning filter: empty Set = show all innings; non-empty = show only selected innings.
  // Multiple innings can be selected simultaneously.
  const [inningFilters, setInningFilters] = useState(() => new Set());

  const [livePos, setLivePos]       = useState(null);
  const [isSnapping, setIsSnapping] = useState(false);
  const snapTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(snapTimerRef.current), []);

  const effectivePos = (isDragging || isSnapping) ? livePos : placedPosition;

  // ── Responsive scale ───────────────────────────────────────────────────
  useEffect(() => {
    const parent = outerRef.current?.parentElement;
    if (!parent) return;
    const obs = new ResizeObserver(([entry]) => {
      const { width: aw, height: ah } = entry.contentRect;
      setScale(Math.min(1, ah / NATURAL_H, aw / ZONE_WIDTH));
    });
    obs.observe(parent);
    return () => obs.disconnect();
  }, []);

  // ── Reveal animation stages ────────────────────────────────────────────
  useEffect(() => {
    if (!revealMode) { setRevealStage(0); return; }
    const t1 = setTimeout(() => setRevealStage(1), 60);
    const t2 = setTimeout(() => setRevealStage(2), 950);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [revealMode]);

  // ── Available innings derived from history ─────────────────────────────
  const availableInnings = useMemo(() => {
    const innings = new Set();
    for (const entry of pitchHistory || []) {
      const { inning } = entryParts(entry);
      if (inning != null) innings.add(inning);
    }
    return Array.from(innings).sort((a, b) => a - b);
  }, [pitchHistory]);

  // Drop stale inning selections when history changes (pitcher sub resets history).
  useEffect(() => {
    setInningFilters(prev => {
      const next = new Set([...prev].filter(i => availableInnings.includes(i)));
      return next.size === prev.size ? prev : next;
    });
  }, [availableInnings]);

  // ── Filtered history (type + inning gates) ─────────────────────────────
  const filteredHistory = useMemo(() => {
    if (!pitchHistory?.length) return [];
    return pitchHistory.filter(entry => {
      const { chip, inning } = entryParts(entry);
      if (!typeFilters.has(chip)) return false;
      // Empty inningFilters = show all; non-empty = only show selected innings.
      if (inningFilters.size > 0 && inning != null && !inningFilters.has(inning)) return false;
      return true;
    });
  }, [pitchHistory, typeFilters, inningFilters]);

  // ── Heat-map canvas ────────────────────────────────────────────────────
  // Radial gradients clipped to the zone rectangle so blobs don't paint outside.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, ZONE_WIDTH + CANVAS_BLEED * 2, ZONE_HEIGHT + CANVAS_BLEED * 2);
    if (!showHeat || !filteredHistory.length) return;
    const blobR = CHIP_RADIUS * 1.3;
    ctx.save();
    // Clip to the zone rectangle (offset by CANVAS_BLEED since canvas is larger than zone).
    ctx.beginPath();
    ctx.rect(CANVAS_BLEED, CANVAS_BLEED, ZONE_WIDTH, ZONE_HEIGHT);
    ctx.clip();
    for (const entry of filteredHistory) {
      const { x, y, chip: chipType } = entryParts(entry);
      const px = x * ZONE_WIDTH + CANVAS_BLEED;
      const py = y * ZONE_HEIGHT + CANVAS_BLEED;
      const color = CHIP_COLORS[(chipType - 1)] || "#ffffff";
      const grad = ctx.createRadialGradient(px, py, 0, px, py, blobR);
      grad.addColorStop(0,    color + "cc");
      grad.addColorStop(0.4,  color + "77");
      grad.addColorStop(1,    color + "00");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(px, py, blobR, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }, [filteredHistory, showHeat]);

  // ── Dot positions ──────────────────────────────────────────────────────
  const dotPositions = useMemo(
    () => (filteredHistory.length ? computeSpreadDots(filteredHistory) : []),
    [filteredHistory],
  );

  // ── Coordinate helpers ─────────────────────────────────────────────────
  const toNormDrag = useCallback((clientX, clientY) => {
    if (!zoneRef.current) return null;
    const r = zoneRef.current.getBoundingClientRect();
    return {
      x: Math.max(-DRAG_OV_X, Math.min(1 + DRAG_OV_X, (clientX - r.left) / r.width)),
      y: Math.max(-DRAG_OV_Y, Math.min(1 + DRAG_OV_Y, (clientY - r.top) / r.height)),
    };
  }, []);

  const clampToZone = useCallback((pos) => ({
    x: Math.max(0, Math.min(1, pos.x)),
    y: Math.max(0, Math.min(1, pos.y)),
  }), []);

  // ── Zone pointer handlers ──────────────────────────────────────────────
  const handleZonePointerDown = useCallback((e) => {
    if (locked || selectedChip === null || revealMode) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    clearTimeout(snapTimerRef.current);
    setIsSnapping(false);
    setIsDragging(true);
    const pos = toNormDrag(e.clientX, e.clientY);
    if (pos) setLivePos(pos);
  }, [locked, selectedChip, revealMode, toNormDrag]);

  const handleZonePointerMove = useCallback((e) => {
    if (!isDragging || locked || revealMode) return;
    const pos = toNormDrag(e.clientX, e.clientY);
    if (pos) setLivePos(pos);
  }, [isDragging, locked, revealMode, toNormDrag]);

  const commitDrag = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    setIsDragging(false);
    const raw     = toNormDrag(clientX, clientY);
    const current = raw || livePos;
    if (!current) return;
    const isOutside = current.x < 0 || current.x > 1 || current.y < 0 || current.y > 1;
    const finalPos  = clampToZone(current);
    if (isOutside) {
      setIsSnapping(true);
      setLivePos(finalPos);
      snapTimerRef.current = setTimeout(() => {
        setIsSnapping(false);
        onPlace?.(finalPos);
        onPlaceFinal?.(finalPos);
      }, 340);
    } else {
      onPlace?.(finalPos);
      onPlaceFinal?.(finalPos);
    }
  }, [isDragging, toNormDrag, livePos, clampToZone, onPlace, onPlaceFinal]);

  const handleZonePointerUp     = useCallback((e) => commitDrag(e.clientX, e.clientY), [commitDrag]);
  const handleZonePointerCancel = useCallback((e) => commitDrag(e.clientX, e.clientY), [commitDrag]);

  // ── Tile drag — window-level listeners prevent dropped events on fast moves ──
  // Refs so the up/cancel handler always closes over the latest callbacks even if
  // onPlace / onPlaceFinal change identity between renders.
  const onPlaceRef     = useRef(onPlace);
  const onPlaceFinalRef = useRef(onPlaceFinal);
  useEffect(() => { onPlaceRef.current = onPlace; },     [onPlace]);
  useEffect(() => { onPlaceFinalRef.current = onPlaceFinal; }, [onPlaceFinal]);

  const handleTilePointerDown = useCallback((e, chipNum) => {
    if (locked || revealMode) return;
    // Pointer capture suppresses spurious pointerout/enter but window listeners
    // are the primary move/up path so capture failure is non-fatal.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    onChipSelect?.(chipNum);
    setTileDrag({ chip: chipNum, clientX: e.clientX, clientY: e.clientY });
  }, [locked, revealMode, onChipSelect]);

  // Window-level handlers — registered only while a tile drag is in flight.
  useEffect(() => {
    if (!tileDrag) return;

    const handleMove = (e) => {
      setTileDrag(prev => prev ? { ...prev, clientX: e.clientX, clientY: e.clientY } : null);
    };

    const handleUp = (e) => {
      if (zoneRef.current) {
        const r = zoneRef.current.getBoundingClientRect();
        // Allow a CHIP_RADIUS margin so a drop near-but-outside the zone still lands.
        if (
          e.clientX >= r.left - CHIP_RADIUS && e.clientX <= r.right  + CHIP_RADIUS &&
          e.clientY >= r.top  - CHIP_RADIUS && e.clientY <= r.bottom + CHIP_RADIUS
        ) {
          const pos = toNormDrag(e.clientX, e.clientY);
          if (pos) {
            const clamped = clampToZone(pos);
            onPlaceRef.current?.(clamped);
            onPlaceFinalRef.current?.(clamped);
          }
        }
      }
      setTileDrag(null);
    };

    window.addEventListener('pointermove', handleMove, { passive: true });
    window.addEventListener('pointerup',     handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup',     handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!tileDrag, toNormDrag, clampToZone]);

  // ── Pitch-type toggle — always keep ≥1 active ─────────────────────────
  const toggleType = useCallback((n) => {
    setTypeFilters(prev => {
      const next = new Set(prev);
      if (next.has(n) && next.size > 1) next.delete(n);
      else next.add(n);
      return next;
    });
  }, []);

  const pitcherAbbr  = PITCH_TYPES[(pitcherChip ?? 1) - 1]?.abbr || "P";
  const batterAbbr   = PITCH_TYPES[(batterChip  ?? 1) - 1]?.abbr || "B";
  const pitcherColor = CHIP_COLORS[(pitcherChip ?? 1) - 1] || "#ffffff";
  const batterColor  = CHIP_COLORS[(batterChip  ?? 1) - 1] || "#ffffff";
  const cursor       = !revealMode && selectedChip !== null && !locked ? "crosshair" : "default";

  const hasHistory       = (pitchHistory?.length ?? 0) > 0;
  const showInningFilter = hasHistory && availableInnings.length > 1;

  const toggleInning = useCallback((inn) => {
    setInningFilters(prev => {
      const next = new Set(prev);
      if (next.has(inn)) next.delete(inn); else next.add(inn);
      return next;
    });
  }, []);

  let statusText = "";
  if (!revealMode) {
    if (locked) statusText = "Locked in";
    else if (effectivePos && selectedChip) statusText = `${PITCH_TYPES[selectedChip - 1]?.abbr} placed — drag to adjust`;
    else if (selectedChip) statusText = `Drag your ${PITCH_TYPES[selectedChip - 1]?.name} into the zone`;
    else statusText = "Grab a pitch chip and drag it into the zone";
  }

  return (
    <div
      ref={outerRef}
      style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* ── Strike zone ── */}
        <div
          ref={zoneRef}
          style={{
            position: "relative",
            width: ZONE_WIDTH,
            height: ZONE_HEIGHT,
            border: "2px solid white",
            touchAction: "none",
            cursor,
            flexShrink: 0,
            overflow: "visible",
          }}
          onPointerDown={handleZonePointerDown}
          onPointerMove={handleZonePointerMove}
          onPointerUp={handleZonePointerUp}
          onPointerCancel={handleZonePointerCancel}
        >
          {/* Heat-map canvas — bleeds past zone edges for soft-edge blobs */}
          <canvas
            ref={canvasRef}
            width={ZONE_WIDTH + CANVAS_BLEED * 2}
            height={ZONE_HEIGHT + CANVAS_BLEED * 2}
            style={{ position: "absolute", top: -CANVAS_BLEED, left: -CANVAS_BLEED, pointerEvents: "none" }}
          />

          <StrikeZoneGrid />

          {/* ── DOTS / HEAT independent toggles — top-right corner of zone ── */}
          <div
            style={{ position: "absolute", top: 5, right: 5, zIndex: 30, display: "flex", gap: 2 }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowDots(p => !p)}
              onPointerDown={(e) => e.stopPropagation()}
              style={pillStyle(showDots, null)}
            >
              DOTS
            </button>
            <button
              onClick={() => setShowHeat(p => !p)}
              onPointerDown={(e) => e.stopPropagation()}
              style={pillStyle(showHeat, null)}
            >
              HEAT
            </button>
          </div>

          {/* Dot history layer */}
          {showDots && dotPositions.map((dot, idx) => {
            const color = CHIP_COLORS[(dot.chipType - 1)] || "#ffffff";
            return (
              <div key={idx} style={{
                position: "absolute",
                left: dot.px - DOT_R,
                top:  dot.py - DOT_R,
                width: DOT_R * 2, height: DOT_R * 2,
                borderRadius: "50%",
                background: color,
                border: "1.5px solid rgba(0,0,0,0.7)",
                boxShadow: `0 0 5px ${color}88`,
                pointerEvents: "none", zIndex: 5,
              }} />
            );
          })}

          {/* Own chip — placement phase only */}
          {!revealMode && (
            <PlacedChip
              pos={effectivePos}
              selectedChip={selectedChip}
              locked={locked}
              dragging={isDragging}
              snapping={isSnapping}
              overhangThreshold={overhangThreshold}
            />
          )}

          {/* ── Reveal panels ── */}
          {revealMode && (
            <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
              <motion.div
                initial={{ x: "-100%", opacity: 0 }}
                animate={{ x: "0%", opacity: revealStage >= 1 ? 1 : 0 }}
                transition={{ type: "spring", stiffness: 155, damping: 22 }}
                style={{ position: "absolute", inset: 0, border: `2px solid ${pitcherColor}77`, background: "rgba(2,6,23,0.35)" }}
              >
                <RevealChip pos={pitcherPosition} chip={pitcherChip} abbr={pitcherAbbr} typeMatch={typeMatch} />
              </motion.div>
              <motion.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: "0%", opacity: revealStage >= 1 ? 1 : 0 }}
                transition={{ type: "spring", stiffness: 155, damping: 22, delay: 0.12 }}
                style={{ position: "absolute", inset: 0, border: `2px solid ${batterColor}77`, background: "rgba(2,6,23,0.25)" }}
              >
                <RevealChip pos={batterPosition} chip={batterChip} abbr={batterAbbr} typeMatch={typeMatch} />
              </motion.div>
              <AnimatePresence>
                {revealStage >= 2 && positionalOverlap && (
                  <PulsingOverlapRing posA={pitcherPosition} posB={batterPosition} />
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ── Chip selector dock (hidden in reveal mode) ── */}
        {!revealMode && (
          <div style={{ marginTop: 14, display: "flex", gap: 10, justifyContent: "center" }}>
            {Array.from({ length: chipCount }, (_, i) => i + 1).map(n => {
              const color = CHIP_COLORS[n - 1];
              const isSelected = selectedChip === n;
              const isDepleted = depletedChips != null && depletedChips.has ? depletedChips.has(n) : (Array.isArray(depletedChips) && depletedChips.includes(n));
              return (
                <button
                  key={n}
                  disabled={locked || isDepleted}
                  onPointerDown={isDepleted ? undefined : (e) => handleTilePointerDown(e, n)}
                  title={isDepleted ? `${PITCH_TYPES[n - 1]?.name} (depleted)` : PITCH_TYPES[n - 1]?.name}
                  style={{
                    width: TILE_DOCK_SIZE, height: TILE_DOCK_SIZE,
                    border: `2px solid ${isDepleted ? "rgba(255,255,255,0.08)" : isSelected ? "white" : color + "99"}`,
                    background: isDepleted ? "rgba(15,23,42,0.6)" : isSelected ? color : color + "55",
                    color: isDepleted ? "rgba(255,255,255,0.2)" : "white",
                    borderRadius: "50%",
                    fontWeight: 900, fontSize: 10,
                    cursor: isDepleted ? "not-allowed" : locked ? "not-allowed" : "grab",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s, transform 0.1s",
                    flexShrink: 0, userSelect: "none", touchAction: "none",
                    transform: isSelected ? "scale(1.16)" : "scale(1)",
                    boxShadow: isDepleted ? "none" : isSelected ? `0 0 12px ${color}88, 0 0 5px ${color}` : `0 0 5px ${color}33`,
                    opacity: locked ? 0.4 : isDepleted ? 0.35 : 1,
                    textDecoration: isDepleted ? "line-through" : "none",
                  }}
                >
                  <span style={{ fontSize: 10, letterSpacing: "0.03em" }}>{PITCH_TYPES[n - 1]?.abbr}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Filter bar: pitch-type visibility toggles ── */}
        {hasHistory && (
          <div style={{
            marginTop: revealMode ? 10 : 8,
            display: "flex", alignItems: "center", gap: 5, justifyContent: "center",
            flexWrap: "wrap", width: ZONE_WIDTH,
          }}>
            {Array.from({ length: chipCount }, (_, i) => i + 1).map(n => {
              const color = CHIP_COLORS[n - 1];
              const active = typeFilters.has(n);
              return (
                <button
                  key={n}
                  onClick={() => toggleType(n)}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={pillStyle(active, color)}
                >
                  {PITCH_TYPES[n - 1]?.abbr}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Inning filter — multi-select; ALL clears selection to show everything ── */}
        {showInningFilter && (
          <div style={{
            marginTop: 6,
            display: "flex", alignItems: "center", gap: 4, justifyContent: "center",
            flexWrap: "wrap", width: ZONE_WIDTH,
          }}>
            <span style={{
              fontSize: 8, fontWeight: 700, letterSpacing: "0.08em",
              color: "rgba(255,255,255,0.3)", textTransform: "uppercase", marginRight: 2,
            }}>
              INN
            </span>
            {/* ALL clears all inning selections → shows every inning */}
            <button
              onClick={() => setInningFilters(new Set())}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ ...pillStyle(inningFilters.size === 0, null), minWidth: 20, padding: "2px 5px" }}
            >
              ALL
            </button>
            {availableInnings.map(inn => (
              <button
                key={inn}
                onClick={() => toggleInning(inn)}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ ...pillStyle(inningFilters.has(inn), null), minWidth: 20, padding: "2px 5px" }}
              >
                {inn}
              </button>
            ))}
          </div>
        )}

        {/* ── Reveal outcome badges ── */}
        {revealMode && revealStage >= 2 && (positionalOverlap !== null || typeMatch !== null) && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ marginTop: 10, display: "flex", gap: 10, justifyContent: "center" }}
          >
            {positionalOverlap !== null && (
              <div className={`px-3 py-1.5 rounded-lg text-xs font-black border ${positionalOverlap ? "border-green-500 text-green-400 bg-green-500/10" : "border-slate-700 text-slate-500 bg-slate-800/40"}`}>
                {positionalOverlap ? "✓ Overlap" : "✗ No Overlap"}
              </div>
            )}
            {typeMatch !== null && (
              <div className={`px-3 py-1.5 rounded-lg text-xs font-black border ${typeMatch ? "border-amber-400 text-amber-300 bg-amber-500/10" : "border-slate-700 text-slate-500 bg-slate-800/40"}`}>
                {typeMatch ? "✓ Right Pitch" : "✗ Wrong Pitch"}
              </div>
            )}
          </motion.div>
        )}

        {/* ── Status text ── */}
        {statusText && (
          <p style={{ marginTop: 8, fontSize: 11, color: "#64748b", fontWeight: 500, textAlign: "center", maxWidth: 240 }}>
            {statusText}
          </p>
        )}
      </div>

      {/* ── Floating ghost chip during tile drag ──────────────────────────────
           Uses `scale` so the ghost matches the placed chip's visual size exactly.
           position:fixed means it lives outside the CSS-scaled container, so we
           apply the scale factor manually to radius and offset.              */}
      {tileDrag && (() => {
        const gr = CHIP_RADIUS * scale;
        const color = CHIP_COLORS[tileDrag.chip - 1];
        return (
          <div style={{
            position: "fixed",
            left: tileDrag.clientX - gr,
            top:  tileDrag.clientY - gr,
            width: gr * 2, height: gr * 2,
            borderRadius: "50%",
            border: `${Math.max(2, 3 * scale)}px solid ${color}`,
            background: color + "99",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: Math.round(13 * scale), fontWeight: 900, color: "white",
            pointerEvents: "none", zIndex: 9999,
            boxShadow: `0 0 ${Math.round(16 * scale)}px ${color}88`,
          }}>
            {PITCH_TYPES[tileDrag.chip - 1]?.abbr}
          </div>
        );
      })()}
    </div>
  );
}
